import { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../config';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';

const TODAY = new Date().toISOString().slice(0, 10);

const emptyFixed = {
    inward_date:            TODAY,
    invoice_challan_number: '',
    po_number:              '',
    grn_number:             '',
    source:                 '',
    source_type:            '',
    in_location:            '',
    updated_by:             '',
};

const emptyRow = { ean_number:'', product_name:'', quantity:'', mfg_date:'', exp_date:'', skuSearch:'', skuDropdown:false, skuHighlight:0, skuPreview:'' };

export default function InwardForm({ user }) {
    const [mode,         setMode]         = useState('single'); // single | bulk
    const [fixed, setFixed] = useState({ ...emptyFixed, updated_by: user?.username || '' });
    const [rows,         setRows]         = useState([{ ...emptyRow }]);
    const [skus,         setSkus]         = useState([]);
    const [sourceTypes,  setSourceTypes]  = useState([]);
    const [locations,    setLocations]    = useState([]);
    const [recent,       setRecent]       = useState([]);
    const [loading,      setLoading]      = useState(false);
    const [submitting,   setSubmitting]   = useState(false);
    const [msg,          setMsg]          = useState(null);
    const [editBatch,    setEditBatch]    = useState(null);
    const [deleteKey,    setDeleteKey]    = useState(null);
    const [expandedBatch,setExpandedBatch]= useState(null);
    const [batchDetails, setBatchDetails] = useState({});

    // Single mode refs
    const [singleForm, setSingleForm] = useState({ ...emptyFixed, updated_by: user?.username || '', ean_number:'', quantity:'', mfg_date:'', exp_date:'' });
    const [selSku,       setSelSku]       = useState(null);
    const [skuSearch,    setSkuSearch]    = useState('');
    const [skuDropdown,  setSkuDropdown]  = useState(false);
    const [skuHighlight, setSkuHighlight] = useState(0);
    const [skuPreview,   setSkuPreview]   = useState('');
    const [editKey,      setEditKey]      = useState(null);

    const dateRef     = useRef();
    const skuRef      = useRef();
    const skuListRef  = useRef();
    const sourceRef   = useRef();
    const srcTypeRef  = useRef();
    const locationRef = useRef();
    const qtyRef      = useRef();
    const challanRef  = useRef();
    const poRef       = useRef();
    const grnRef      = useRef();
    const mfgRef      = useRef();
    const expRef      = useRef();
    const byRef       = useRef();

    useEffect(() => { loadData(); }, []);
    useEffect(() => { setTimeout(() => dateRef.current?.focus(), 300); }, [mode]);

    async function loadData() {
        setLoading(true);
        try {
            const d = await fetch(`${API_BASE}/inventory-inward`).then(r => r.json());
            setSkus(d.skus           || []);
            setSourceTypes(d.source_types || []);
            setLocations(d.locations      || []);
            setRecent(d.recent            || []);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    }

    // ── Bulk helpers ─────────────────────────────────────────────────────────
    function handleFixed(key, val) { setFixed(f => ({ ...f, [key]: val })); }

    function handleRow(idx, key, val) {
        setRows(rs => rs.map((r, i) => i === idx ? { ...r, [key]: val } : r));
    }

    function addRow() {
        setRows(rs => [...rs, { ...emptyRow }]);
    }

    function removeRow(idx) {
        setRows(rs => rs.filter((_, i) => i !== idx));
    }

    function selectSkuForRow(idx, sku) {
        setRows(rs => rs.map((r, i) => i === idx ? {
            ...r,
            ean_number:   String(sku.ean_number),
            product_name: sku.product_name,
            skuSearch:    sku.product_name,
            skuDropdown:  false,
            skuHighlight: 0,
            skuPreview:   ''
        } : r));
    }

    function handleRowSkuKey(e, idx) {
    const row      = rows[idx];
    const filtered = getFilteredSkus(row.skuSearch);
    if (!row.skuDropdown || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = Math.min(row.skuHighlight + 1, filtered.length - 1);
        handleRow(idx, 'skuHighlight', next);
        handleRow(idx, 'skuPreview', filtered[next].product_name);
        setTimeout(() => {
            const dd = document.getElementById(`row-dd-${idx}`);
            if (dd) dd.children[next]?.scrollIntoView({ block: 'nearest' });
        }, 10);
    } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = Math.max(row.skuHighlight - 1, 0);
        handleRow(idx, 'skuHighlight', prev);
        handleRow(idx, 'skuPreview', filtered[prev].product_name);
        setTimeout(() => {
            const dd = document.getElementById(`row-dd-${idx}`);
            if (dd) dd.children[prev]?.scrollIntoView({ block: 'nearest' });
        }, 10);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectSkuForRow(idx, filtered[row.skuHighlight]);
    } else if (e.key === 'Escape') {
        handleRow(idx, 'skuDropdown', false);
    }
}

    function getFilteredSkus(search) {
        if (!search || search.length < 2) return [];
        return skus.filter(s =>
            s.product_name?.toLowerCase().includes(search.toLowerCase()) ||
            String(s.ean_number).includes(search)
        ).slice(0, 12);
    }

    async function handleBulkSave() {
        if (!fixed.source)      { setMsg({ type:'error', text:'Please enter source' });        return; }
        if (!fixed.source_type) { setMsg({ type:'error', text:'Please select source type' });  return; }
        if (!fixed.in_location) { setMsg({ type:'error', text:'Please select location' });     return; }
        if (!fixed.updated_by)  { setMsg({ type:'error', text:'Please enter your name' });     return; }

        const validRows = rows.filter(r => r.ean_number && r.quantity && parseInt(r.quantity) > 0);
        if (validRows.length === 0) { setMsg({ type:'error', text:'Please add at least one SKU with quantity' }); return; }

        setSubmitting(true); setMsg(null);
        try {
            const payload = editBatch
                ? { batch_id: editBatch, fixed, rows: validRows }
                : { fixed, rows: validRows };

            const res  = await fetch(`${API_BASE}/inventory-inward`, {
                method: editBatch ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setMsg({ type:'success', text: data.message });
                setFixed(emptyFixed);
                setRows([{ ...emptyRow }]);
                setEditBatch(null);
                await loadData();
                setTimeout(() => dateRef.current?.focus(), 100);
            } else {
                setMsg({ type:'error', text: typeof data === 'string' ? data : 'Failed to save' });
            }
        } catch(err) {
            setMsg({ type:'error', text:'Network error: ' + err.message });
        } finally { setSubmitting(false); }
    }

    // ── Single mode helpers ──────────────────────────────────────────────────
    function handleSingle(key, val) { setSingleForm(f => ({ ...f, [key]: val })); }

    function selectSku(sku) {
        setSelSku(sku);
        setSingleForm(f => ({ ...f, ean_number: String(sku.ean_number) }));
        setSkuSearch(sku.product_name);
        setSkuDropdown(false); setSkuHighlight(0); setSkuPreview('');
        setTimeout(() => sourceRef.current?.focus(), 50);
    }

    function handleSkuKey(e) {
        const filtered = getFilteredSkus(skuSearch);
        if (!skuDropdown || filtered.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = Math.min(skuHighlight + 1, filtered.length - 1);
            setSkuHighlight(next); setSkuPreview(filtered[next].product_name);
            skuListRef.current?.children[next]?.scrollIntoView({ block:'nearest' });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prev = Math.max(skuHighlight - 1, 0);
            setSkuHighlight(prev); setSkuPreview(filtered[prev].product_name);
            skuListRef.current?.children[prev]?.scrollIntoView({ block:'nearest' });
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault(); selectSku(filtered[skuHighlight]); setSkuPreview('');
        } else if (e.key === 'Escape') { setSkuDropdown(false); setSkuPreview(''); }
    }

    function handleSelectKey(e, nextRef) {
        if (e.key === 'Enter') { e.preventDefault(); nextRef?.current?.focus(); }
    }

    function startSingleEdit(row) {
        setEditKey(row.update_time_key);
        setSingleForm({
            inward_date:            row.inward_date            || TODAY,
            invoice_challan_number: row.invoice_challan_number || '',
            po_number:              row.po_number              || '',
            grn_number:             row.grn_number             || '',
            source:                 row.source                 || '',
            source_type:            row.source_type            || '',
            ean_number:             row.ean_number             || '',
            in_location:            row.in_location            || '',
            quantity:               row.quantity               || '',
            mfg_date:               row.mfg_date               || '',
            exp_date:               row.exp_date               || '',
            updated_by:             row.updated_by             || '',
        });
        setSkuSearch(skus.find(s => String(s.ean_number) === String(row.ean_number))?.product_name || row.ean_number);
        setMsg({ type:'info', text:`Editing entry from ${row.inward_date}` });
        window.scrollTo({ top:0, behavior:'smooth' });
        setTimeout(() => dateRef.current?.focus(), 400);
    }

    async function startBatchEdit(batch) {
        // Fetch batch rows
        const res  = await fetch(`${API_BASE}/inventory-inward?batch_id=${batch.batch_id}`);
        const data = await res.json();
        setFixed({
            inward_date:            batch.inward_date            || TODAY,
            invoice_challan_number: batch.invoice_challan_number || '',
            po_number:              batch.po_number              || '',
            grn_number:             batch.grn_number             || '',
            source:                 batch.source                 || '',
            source_type:            batch.source_type            || '',
            in_location:            batch.in_location            || '',
            updated_by:             batch.updated_by             || '',
        });
        setRows(data.map(r => ({
            ean_number:   r.ean_number,
            product_name: skus.find(s => String(s.ean_number) === String(r.ean_number))?.product_name || r.ean_number,
            quantity:     r.quantity,
            mfg_date:     r.mfg_date || '',
            exp_date:     r.exp_date || '',
            skuSearch:    skus.find(s => String(s.ean_number) === String(r.ean_number))?.product_name || r.ean_number,
            skuDropdown:  false, skuHighlight:0, skuPreview:''
        })));
        setEditBatch(batch.batch_id);
        setMode('bulk');
        setMsg({ type:'info', text:`Editing batch — ${batch.sku_count} SKUs` });
        window.scrollTo({ top:0, behavior:'smooth' });
    }

    async function handleSingleSubmit() {
        if (!singleForm.ean_number)  { setMsg({ type:'error', text:'Please select a SKU' });       skuRef.current?.focus();      return; }
        if (!singleForm.source)      { setMsg({ type:'error', text:'Please enter source' });        sourceRef.current?.focus();   return; }
        if (!singleForm.source_type) { setMsg({ type:'error', text:'Please select source type' }); srcTypeRef.current?.focus();  return; }
        if (!singleForm.in_location) { setMsg({ type:'error', text:'Please select location' });    locationRef.current?.focus(); return; }
        if (!singleForm.quantity || parseInt(singleForm.quantity) <= 0) {
            setMsg({ type:'error', text:'Enter valid quantity' }); qtyRef.current?.focus(); return;
        }
        if (!singleForm.updated_by) { setMsg({ type:'error', text:'Enter your name' }); byRef.current?.focus(); return; }

        setSubmitting(true); setMsg(null);
        try {
            const isEdit = !!editKey;
            const payload = isEdit ? { ...singleForm, update_time_key: editKey } : singleForm;
            const res = await fetch(`${API_BASE}/inventory-inward`, {
                method: isEdit ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setMsg({ type:'success', text: isEdit ? '✓ Entry updated!' : '✓ Entry saved!' });
                const savedName = singleForm.updated_by;
                setSingleForm({ ...emptyFixed, updated_by: savedName, ean_number:'', quantity:'', mfg_date:'', exp_date:'' });
                setSelSku(null); setSkuSearch(''); setSkuPreview(''); setEditKey(null);
                await loadData();
                setTimeout(() => { dateRef.current?.focus(); }, 100);
            } else {
                setMsg({ type:'error', text: typeof data === 'string' ? data : 'Failed' });
            }
        } catch(err) {
            setMsg({ type:'error', text:'Network error: ' + err.message });
        } finally { setSubmitting(false); }
    }

    async function handleDelete(key, batchId) {
        try {
            const url = batchId
                ? `${API_BASE}/inventory-inward?batch_id=${encodeURIComponent(batchId)}`
                : `${API_BASE}/inventory-inward?key=${encodeURIComponent(key)}`;
            const res  = await fetch(url, { method:'DELETE' });
            const data = await res.json();
            if (res.ok && data.success) {
                setMsg({ type:'success', text:'✓ Deleted' });
                setDeleteKey(null);
                await loadData();
            }
        } catch(err) { setMsg({ type:'error', text:'Delete failed' }); }
    }

    async function toggleBatchExpand(batch) {
        if (expandedBatch === batch.batch_id) {
            setExpandedBatch(null); return;
        }
        setExpandedBatch(batch.batch_id);
        if (!batchDetails[batch.batch_id]) {
            const res  = await fetch(`${API_BASE}/inventory-inward?batch_id=${batch.batch_id}`);
            const data = await res.json();
            setBatchDetails(d => ({ ...d, [batch.batch_id]: data }));
        }
    }

    const validRowCount = rows.filter(r => r.ean_number && r.quantity).length;

    function handleGlobalKey(e) {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            mode === 'bulk' ? handleBulkSave() : handleSingleSubmit();
        }
    }

    return (
        <div onKeyDown={handleGlobalKey}>
           <div style={{ marginBottom: '20px' }}>
    <h1 style={{ fontSize:'30px', fontWeight:800, color:'#1A1A1A', margin:0 }}>Inward Entry</h1>
               
</div>

            {/* Mode toggle */}
            <div style={{
                display:'inline-flex', background:'#F1F5F9',
                borderRadius:'10px', padding:'4px', marginBottom:'20px',
                border:'1px solid #E2E8F0'
            }}>
                {['single','bulk'].map(m => (
                    <button key={m} onClick={() => { setMode(m); setMsg(null); setEditKey(null); setEditBatch(null); }}
                        style={{
                            padding:'8px 24px', borderRadius:'7px', border:'none',
                            background: mode === m ? '#5a5a5a' : 'transparent',
                            color: mode === m ? '#fff' : '#64748B',
                            fontWeight: mode === m ? 700 : 500,
                            fontSize:'15px', cursor:'pointer', transition:'all 0.15s',
                            textTransform:'capitalize'
                        }}>
                        {m === 'single' ? 'Single Entry' : 'Bulk Entry'}
                    </button>
                ))}
            </div>

            {/* ── SINGLE MODE ── */}
            {mode === 'single' && (
                <div data-tutorial="inward-form" style={{
                    background:'#fff', borderRadius:'14px',
                    boxShadow: editKey ? '0 0 0 3px #c462f9,0 2px 12px rgba(0,0,0,0.08)' : '0 2px 12px rgba(0,0,0,0.08)',
                    padding:'24px 28px', marginBottom:'20px'
                }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
                        <h3 style={{ fontSize:'25px', fontWeight:700, color: editKey?'#00C9A7':'#0F1F2E' }}>
                            {editKey ? '✏️ Edit Entry' : 'New Single Inward Entry'}
                        </h3>
                        {msg && <MsgBox msg={msg} />}
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'200px 1fr', gap:'14px', marginBottom:'14px' }}>
                        <Field label="Date *">
                            <input ref={dateRef} type="date" style={iStyle}
                                value={singleForm.inward_date}
                                onChange={e => handleSingle('inward_date', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'||e.key==='Tab'){e.preventDefault();skuRef.current?.focus();}}}
                            />
                        </Field>
                        <Field label="SKU / EAN *" hint={selSku ? `✓ EAN: ${selSku.ean_number} · ${selSku.product || ''}` : ''}>
                            <div style={{ position:'relative' }}>
                                <input ref={skuRef} type="text" style={iStyle}
                                    placeholder={skuPreview || "Type product name or EAN..."}
                                    value={skuSearch}
                                    onChange={e => {
                                        setSkuSearch(e.target.value); setSkuDropdown(true);
                                        setSkuHighlight(0); setSkuPreview('');
                                        setSelSku(null); handleSingle('ean_number','');
                                    }}
                                    onFocus={() => skuSearch.length >= 2 && setSkuDropdown(true)}
                                    onBlur={() => setTimeout(() => { setSkuDropdown(false); setSkuPreview(''); }, 150)}
                                    onKeyDown={handleSkuKey}
                                />
                                <SkuDropdown
                                    listRef={skuListRef}
                                    items={getFilteredSkus(skuSearch)}
                                    show={skuDropdown}
                                    highlight={skuHighlight}
                                    onSelect={selectSku}
                                    color="#d079f6"
                                    bg="#E6FAF7"
                                />
                            </div>
                        </Field>
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'14px', marginBottom:'14px' }}>
                        <Field label="Source *">
                            <input ref={sourceRef} type="text" style={iStyle}
                                placeholder="Vendor / supplier" value={singleForm.source}
                                onChange={e => handleSingle('source', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();srcTypeRef.current?.focus();}}}
                            />
                        </Field>
                        <Field label="Source Type *">
                            <select ref={srcTypeRef} style={iStyle} value={singleForm.source_type}
                                onChange={e => handleSingle('source_type', e.target.value)}
                                onKeyDown={e => handleSelectKey(e, locationRef)}>
                                <option value="">Select type</option>
                                {sourceTypes.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Warehouse Location *">
                            <select ref={locationRef} style={iStyle} value={singleForm.in_location}
                                onChange={e => handleSingle('in_location', e.target.value)}
                                onKeyDown={e => handleSelectKey(e, qtyRef)}>
                                <option value="">Select location</option>
                                {locations.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>
                        </Field>
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'14px', marginBottom:'14px' }}>
                        <Field label="Quantity *">
                            <input ref={qtyRef} type="number" style={iStyle} min="1"
                                placeholder="Units" value={singleForm.quantity}
                                onChange={e => handleSingle('quantity', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();challanRef.current?.focus();}}}
                            />
                        </Field>
                        <Field label="Invoice / Challan No.">
                            <input ref={challanRef} type="text" style={iStyle} placeholder="Optional"
                                value={singleForm.invoice_challan_number}
                                onChange={e => handleSingle('invoice_challan_number', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();poRef.current?.focus();}}}
                            />
                        </Field>
                        <Field label="PO Number">
                            <input ref={poRef} type="text" style={iStyle} placeholder="Optional"
                                value={singleForm.po_number}
                                onChange={e => handleSingle('po_number', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();grnRef.current?.focus();}}}
                            />
                        </Field>
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'14px', marginBottom:'20px' }}>
                        <Field label="GRN Number">
                            <input ref={grnRef} type="text" style={iStyle} placeholder="Optional"
                                value={singleForm.grn_number}
                                onChange={e => handleSingle('grn_number', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();mfgRef.current?.focus();}}}
                            />
                        </Field>
                        <Field label="Mfg Date">
                            <input ref={mfgRef} type="date" style={iStyle}
                                value={singleForm.mfg_date}
                                onChange={e => handleSingle('mfg_date', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();expRef.current?.focus();}}}
                            />
                        </Field>
                        <Field label="Expiry Date">
                            <input ref={expRef} type="date" style={iStyle}
                                value={singleForm.exp_date}
                                onChange={e => handleSingle('exp_date', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();byRef.current?.focus();}}}
                            />
                        </Field>
                       <Field label="Your Name">
    <input ref={byRef} type="text" style={{ ...iStyle, background:'#F1F5F9', color:'#64748B' }}
        value={singleForm.updated_by}
        readOnly
    />
</Field>
                    </div>

                    <ActionRow
                        submitting={submitting} isEdit={!!editKey}
                        onSave={() => { if (!hasEditAccess(user, 'inventory', 'inward')) return notifyEditBlocked(); handleSingleSubmit(); }}
                        onClear={() => {
                            if (editKey) { setEditKey(null); }
                            const n = singleForm.updated_by;
                            setSingleForm({ ...emptyFixed, updated_by:n, ean_number:'', quantity:'', mfg_date:'', exp_date:'' });
                            setSelSku(null); setSkuSearch(''); setSkuPreview(''); setMsg(null);
                            dateRef.current?.focus();
                        }}
                        saveLabel={editKey ? 'Update' : 'Save'}
                    />
                </div>
            )}

            {/* ── BULK MODE ── */}
            {mode === 'bulk' && (
                <div style={{
                    background:'#fff', borderRadius:'14px',
                    boxShadow: editBatch ? '0 0 0 3px #c03aff,0 2px 12px rgba(0,0,0,0.08)' : '0 2px 12px rgba(0,0,0,0.08)',
                    padding:'24px 28px', marginBottom:'20px'
                }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
                        <h3 style={{ fontSize:'25px', fontWeight:700, color: editBatch?'#a23ff8':'#0F1F2E' }}>
                            {editBatch ? ' Edit Batch' : 'Bulk Inward Entry'}
                        </h3>
                        {msg && <MsgBox msg={msg} />}
                    </div>

                    {/* Fixed fields */}
                    <div style={{
                        background:'#F8FAFC', borderRadius:'10px',
                        padding:'16px 20px', marginBottom:'20px',
                        border:'1px solid #E2E8F0'
                    }}>
                        <div style={{ fontSize:'15px', fontWeight:700, color:'#64748B', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'12px' }}>
                            Common Fields (apply to all SKUs)
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'12px', marginBottom:'12px' }}>
                            <Field label="Date *">
                                <input ref={dateRef} type="date" style={iStyle}
                                    value={fixed.inward_date}
                                    onChange={e => handleFixed('inward_date', e.target.value)} />
                            </Field>
                            <Field label="Source *">
                                <input type="text" style={iStyle} placeholder="Vendor name"
                                    value={fixed.source}
                                    onChange={e => handleFixed('source', e.target.value)} />
                            </Field>
                            <Field label="Source Type *">
                                <select style={iStyle} value={fixed.source_type}
                                    onChange={e => handleFixed('source_type', e.target.value)}>
                                    <option value="">Select</option>
                                    {sourceTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </Field>
                            <Field label="Location *">
                                <select style={iStyle} value={fixed.in_location}
                                    onChange={e => handleFixed('in_location', e.target.value)}>
                                    <option value="">Select</option>
                                    {locations.map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                            </Field>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'12px' }}>
                            <Field label="Invoice / Challan">
                                <input type="text" style={iStyle} placeholder="Optional"
                                    value={fixed.invoice_challan_number}
                                    onChange={e => handleFixed('invoice_challan_number', e.target.value)} />
                            </Field>
                            <Field label="PO Number">
                                <input type="text" style={iStyle} placeholder="Optional"
                                    value={fixed.po_number}
                                    onChange={e => handleFixed('po_number', e.target.value)} />
                            </Field>
                            <Field label="GRN Number">
                                <input type="text" style={iStyle} placeholder="Optional"
                                    value={fixed.grn_number}
                                    onChange={e => handleFixed('grn_number', e.target.value)} />
                            </Field>
                          <Field label="Your Name">
    <input ref={byRef} type="text" style={{ ...iStyle, background:'#F1F5F9', color:'#64748B' }}
        value={singleForm.updated_by}
        readOnly
    />
</Field>
                        </div>
                    </div>

                    {/* SKU rows table */}
                    <div style={{ marginBottom:'16px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                            <div style={{ fontSize:'18px', fontWeight:700, color:'#0F1F2E' }}>
                                SKU List
                                <span style={{ marginLeft:'8px', fontSize:'15px', color:'#4d0070', fontWeight:600 }}>
                                    {validRowCount} ready
                                </span>
                            </div>
                        
                        </div>

                       <div style={{ overflowX:'auto', overflowY:'visible' }}>
    <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'700px', overflow:'visible' }}>
                                <thead>
                                    <tr>
                                        {['#','SKU / Product Name','EAN','Qty *','Mfg Date','Exp Date','Action'].map(h => (
                                            <th key={h} style={{
                                                background:'#0F1F2E', color:'#fff',
                                                padding:'9px 12px', textAlign:'left',
                                                fontSize:'15px', fontWeight:600, whiteSpace:'nowrap'
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, idx) => {
                                        const filtered = getFilteredSkus(row.skuSearch);
                                        return (
                                            <tr key={idx} style={{ background: idx%2===0?'#fff':'#F8FAFC' }}>
                                                <td style={{ ...rtd, width:'40px', color:'#94A3B8', fontWeight:600 }}>{idx+1}</td>
                                               <td style={{ ...rtd, minWidth:'260px', position:'relative' }}>
    <input type="text" style={{ ...iStyle, fontSize:'18px' }}
        id={`sku-input-${idx}`}
        placeholder={row.skuPreview || "Search SKU..."}
        value={row.skuSearch}
        onChange={e => {
            handleRow(idx,'skuSearch',e.target.value);
            handleRow(idx,'skuDropdown',true);
            handleRow(idx,'skuHighlight',0);
            handleRow(idx,'skuPreview','');
            handleRow(idx,'ean_number','');
        }}
        onFocus={() => row.skuSearch.length>=2 && handleRow(idx,'skuDropdown',true)}
        onBlur={() => setTimeout(()=>handleRow(idx,'skuDropdown',false),150)}
        onKeyDown={e => handleRowSkuKey(e,idx)}
    />
    {row.skuDropdown && filtered.length > 0 && (() => {
        const el = document.getElementById(`sku-input-${idx}`);
        const rect = el ? el.getBoundingClientRect() : null;
        return rect ? (
           <div id={`row-dd-${idx}`} style={{
    position:'fixed',
    top: rect.bottom + 2,
    left: rect.left,
    width: rect.width,
    zIndex: 9999,
                background:'#fff', borderRadius:'8px',
                boxShadow:'0 8px 24px rgba(0,0,0,0.15)',
                border:'1px solid #E2E8F0',
                maxHeight:'220px', overflowY:'auto'
            }}>
                {filtered.map((s,i) => (
                    <div key={s.ean_number}
                        onMouseDown={() => selectSkuForRow(idx,s)}
                        style={{
                            padding:'8px 12px', cursor:'pointer',
                            display:'flex', justifyContent:'space-between',
                            background: i===row.skuHighlight?'#E6FAF7':'#fff',
                            borderLeft: i===row.skuHighlight?'3px solid #be6eec':'3px solid transparent',
                            borderBottom:'1px solid #F1F5F9', fontSize:'15px'
                        }}>
                        <span style={{ fontWeight: i===row.skuHighlight?600:400 }}>{s.product_name}</span>
                        <span style={{ fontSize:'15px', color:'#94A3B8' }}>{s.ean_number}</span>
                    </div>
                ))}
            </div>
        ) : null;
    })()}
</td>
                                                <td style={{ ...rtd, fontSize:'15px',fontWeight:'500', color:'#64748B', width:'130px' }}>
                                                    {row.ean_number || '—'}
                                                </td>
                                                <td style={{ ...rtd, width:'140px' }}>
                                                    <input type="number" min="1"
                                                        style={{ ...iStyle, fontSize:'18px' }}
                                                        placeholder="Qty"
                                                        value={row.quantity}
                                                        onChange={e => handleRow(idx,'quantity',e.target.value)}
                                                        onKeyDown={e => {}}
                                                    />
                                                </td>
                                                <td style={{ ...rtd, width:'130px' }}>
                                                    <input type="date" style={{ ...iStyle, fontSize:'18px' }}
                                                        value={row.mfg_date}
                                                        onChange={e => handleRow(idx,'mfg_date',e.target.value)} />
                                                </td>
                                                <td style={{ ...rtd, width:'130px' }}>
                                                    <input type="date" style={{ ...iStyle, fontSize:'18px' }}
                                                        value={row.exp_date}
                                                        onChange={e => handleRow(idx,'exp_date',e.target.value)} />
                                                </td>
                                                <td style={{ ...rtd, width:'80px', textAlign:'center', whiteSpace:'nowrap' }}>
    {idx === rows.length - 1 && (
        <button onClick={addRow} style={{
            background:'#E6FAF7', color:'#065F46',
            border:'1.5px solid #5a00c9', borderRadius:'5px',
            width:'28px', height:'28px', cursor:'pointer',
            fontWeight:700, fontSize:'15px', marginRight:'4px'
        }}>+</button>
    )}
    {rows.length > 1 && (
        <button onClick={() => removeRow(idx)} style={{
            background:'#FEE2E2', color:'#991B1B',
            border:'none', borderRadius:'5px',
            width:'28px', height:'28px', cursor:'pointer',
            fontWeight:700, fontSize:'15px'
        }}>✕</button>
    )}
</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <ActionRow
                        submitting={submitting} isEdit={!!editBatch}
                        onSave={() => { if (!hasEditAccess(user, 'inventory', 'inward')) return notifyEditBlocked(); handleBulkSave(); }}
                        onClear={() => {
                            setFixed(emptyFixed); setRows([{...emptyRow}]);
                            setEditBatch(null); setMsg(null);
                            dateRef.current?.focus();
                        }}
                        saveLabel={editBatch
                            ? `✓ Update Batch`
                            : `✓ Save ${validRowCount} SKU${validRowCount!==1?'s':''}`}
                        color="linear-gradient(135deg,#1A1A1A,#3D3D3D)"
                    />
                </div>
            )}

            {/* Delete modal */}
            {deleteKey && (
                <div style={{
                    position:'fixed',top:0,left:0,right:0,bottom:0,
                    background:'rgba(0,0,0,0.5)',zIndex:500,
                    display:'flex',alignItems:'center',justifyContent:'center'
                }}>
                    <div style={{
                        background:'#fff',borderRadius:'14px',padding:'28px 32px',
                        maxWidth:'400px',width:'90%',boxShadow:'0 8px 32px rgba(0,0,0,0.2)'
                    }}>
                        <h3 style={{ fontSize:'16px',fontWeight:700,color:'#0F1F2E',marginBottom:'10px' }}>Delete Entry?</h3>
                        <p style={{ fontSize:'13px',color:'#64748B',marginBottom:'20px' }}>
                            {deleteKey.batch_id ? `This will delete all ${deleteKey.sku_count} SKUs in this batch.` : 'This entry will be permanently deleted.'}
                        </p>
                        <div style={{ display:'flex',gap:'10px' }}>
                            <button onClick={() => { if (!hasEditAccess(user, 'inventory', 'inward')) return notifyEditBlocked(); handleDelete(deleteKey.key, deleteKey.batch_id); }} style={{
                                padding:'10px 24px',background:'#EF4444',color:'#fff',
                                border:'none',borderRadius:'8px',fontSize:'14px',fontWeight:700,cursor:'pointer'
                            }}>Yes, Delete</button>
                            <button onClick={() => setDeleteKey(null)} style={{
                                padding:'10px 20px',background:'#F1F5F9',color:'#475569',
                                border:'1.5px solid #E2E8F0',borderRadius:'8px',fontSize:'14px',fontWeight:600,cursor:'pointer'
                            }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Recent entries */}
            <div style={{ background:'#fff',borderRadius:'14px',boxShadow:'0 2px 8px rgba(0,0,0,0.07)',overflow:'hidden' }}>
                <div style={{ padding:'14px 20px',borderBottom:'1px solid #F1F5F9',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                    <h3 style={{ fontSize:'15px',fontWeight:700,color:'#0F1F2E' }}>Recent Inward Entries</h3>
                    <span style={{ fontSize:'12px',color:'#94A3B8' }}>{recent.length} entries</span>
                </div>
                <table style={{ width:'100%',borderCollapse:'collapse',fontSize:'13px' }}>
                    <thead>
                        <tr>
                            {['Actions','Date','Challan','PO','GRN','Source','Type','Location','SKUs','Total Qty','By','Time'].map(h => (
                                <th key={h} style={{
                                    background:'#0F1F2E',color:'#fff',padding:'9px 12px',
                                    textAlign:'left',fontSize:'11.5px',fontWeight:600,whiteSpace:'nowrap'
                                }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {recent.length === 0 ? (
                            <tr><td colSpan={12} style={{ padding:'30px',textAlign:'center',color:'#94A3B8' }}>No entries yet</td></tr>
                        ) : recent.map((r,i) => (
                            <>
                                <tr key={i} style={{ background: i%2===0?'#fff':'#F8FAFC' }}>
                                    <td style={{ ...rtd, whiteSpace:'nowrap' }}>
                                        <button onClick={() => { if (!hasEditAccess(user, 'inventory', 'inward')) return notifyEditBlocked(); r.batch_id ? startBatchEdit(r) : startSingleEdit(r); }}
                                            style={{ padding:'4px 8px',background:'#EFF6FF',color:'#1D4ED8',border:'1px solid #BFDBFE',borderRadius:'5px',fontSize:'11px',fontWeight:600,cursor:'pointer',marginRight:'4px' }}>
                                            ✏
                                        </button>
                                        <button onClick={() => setDeleteKey({ key: r.update_time_key, batch_id: r.batch_id, sku_count: r.sku_count })}
                                            style={{ padding:'4px 8px',background:'#FEE2E2',color:'#991B1B',border:'1px solid #FECACA',borderRadius:'5px',fontSize:'11px',fontWeight:600,cursor:'pointer',marginRight:'4px' }}>
                                            🗑
                                        </button>
                                        {r.batch_id && (
                                            <button onClick={() => toggleBatchExpand(r)}
                                                style={{ padding:'4px 8px',background:'#F0FDF4',color:'#065F46',border:'1px solid #BBF7D0',borderRadius:'5px',fontSize:'11px',fontWeight:600,cursor:'pointer' }}>
                                                {expandedBatch === r.batch_id ? '▲' : '▼'}
                                            </button>
                                        )}
                                    </td>
                                    <td style={rtd}>{r.inward_date}</td>
                                    <td style={rtd}>{r.invoice_challan_number || '—'}</td>
                                    <td style={rtd}>{r.po_number || '—'}</td>
                                    <td style={rtd}>{r.grn_number || '—'}</td>
                                    <td style={rtd}>{r.source}</td>
                                    <td style={rtd}>{r.source_type}</td>
                                    <td style={rtd}>{r.in_location}</td>
                                    <td style={{ ...rtd,fontWeight:700,color: r.batch_id?'#1D4ED8':'#334155' }}>
                                        {r.batch_id ? `${r.sku_count} SKUs` : '1 SKU'}
                                    </td>
                                    <td style={{ ...rtd,fontWeight:700,color:'#065F46' }}>{r.total_quantity}</td>
                                    <td style={rtd}>{r.updated_by}</td>
                                    <td style={{ ...rtd,fontSize:'11px',color:'#94A3B8' }}>{r.update_time}</td>
                                </tr>
                                {/* Expanded batch rows */}
                                {expandedBatch === r.batch_id && batchDetails[r.batch_id] && (
                                    batchDetails[r.batch_id].map((d,di) => (
                                        <tr key={`d-${di}`} style={{ background:'#F0FDF4' }}>
                                            <td style={rtd}></td>
                                            <td style={{ ...rtd,paddingLeft:'24px',color:'#64748B',fontSize:'15px' }}>↳</td>
                                            <td colSpan={2} style={{ ...rtd,fontSize:'15px',color:'#065F46',fontWeight:600 }}>
                                                {skus.find(s=>String(s.ean_number)===String(d.ean_number))?.product_name || d.ean_number}
                                            </td>
                                            <td style={{ ...rtd,fontSize:'11px',color:'#94A3B8' }}>{d.ean_number}</td>
                                            <td colSpan={2} style={{ ...rtd,fontWeight:700,color:'#065F46' }}>Qty: {d.quantity}</td>
                                            <td style={rtd}>{d.mfg_date || '—'}</td>
                                            <td style={rtd}>{d.exp_date || '—'}</td>
                                            <td colSpan={3}></td>
                                        </tr>
                                    ))
                                )}
                            </>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Shared sub-components ─────────────────────────────────────────────────────
function Field({ label, hint, children }) {
    return (
        <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <label style={{ fontSize:'15px', fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.6px' }}>{label}</label>
                {hint && <span style={{ fontSize:'11px', color:'#00C9A7', fontWeight:600 }}>{hint}</span>}
            </div>
            {children}
        </div>
    );
}

function MsgBox({ msg }) {
    return (
        <div style={{
            padding:'8px 16px', borderRadius:'8px',
            background: msg.type==='success'?'#D1FAE5':msg.type==='info'?'#EFF6FF':'#FEE2E2',
            color:      msg.type==='success'?'#065F46':msg.type==='info'?'#1D4ED8':'#991B1B',
            fontWeight:600, fontSize:'13px'
        }}>{msg.text}</div>
    );
}

function SkuDropdown({ listRef, items, show, highlight, onSelect, color, bg }) {
    if (!show || items.length === 0) return null;
    return (
       <div style={{
    position:'absolute', top:'100%', left:0, right:0, zIndex:999,
            background:'#fff', borderRadius:'8px',
            boxShadow:'0 8px 24px rgba(0,0,0,0.15)',
            border:'1px solid #E2E8F0', maxHeight:'240px', overflowY:'auto'
        }}>
            {items.map((s,i) => (
                <div key={s.ean_number} onMouseDown={() => onSelect(s)}
                    style={{
                        padding:'9px 14px', display:'flex', justifyContent:'space-between', alignItems:'center',
                        background: i===highlight ? bg : '#fff',
                        borderBottom:'1px solid #F1F5F9',
                        borderLeft: i===highlight ? `3px solid ${color}` : '3px solid transparent',
                        cursor:'pointer'
                    }}>
                    <span style={{ fontSize:'13px', color:'#1E293B', fontWeight: i===highlight?600:400 }}>{s.product_name}</span>
                    <span style={{ fontSize:'11px', color:'#94A3B8', marginLeft:'12px' }}>{s.ean_number}</span>
                </div>
            ))}
        </div>
    );
}

function ActionRow({ submitting, isEdit, onSave, onClear, saveLabel, color }) {
    return (
        <div style={{ display:'flex', gap:'12px', alignItems:'center' }}>
            <button onClick={onSave} disabled={submitting} style={{
                padding:'11px 28px',
                background: submitting ? '#94A3B8' : (color || 'linear-gradient(135deg,#1A1A1A,#3D3D3D)'),
                color:'#fff', border:'none', borderRadius:'8px',
                fontSize:'14px', fontWeight:700,
                cursor: submitting?'not-allowed':'pointer',
                boxShadow:'0 2px 6px rgba(0,201,167,0.3)'
            }}>
                {submitting ? 'Saving...' : saveLabel}
            </button>
            <button onClick={onClear} style={{
                padding:'11px 20px', background:'#F1F5F9', color:'#475569',
                border:'1.5px solid #E2E8F0', borderRadius:'8px',
                fontSize:'14px', fontWeight:600, cursor:'pointer'
            }}>
                {isEdit ? 'Cancel' : 'Clear'}
            </button>
            
        </div>
    );
}

const iStyle = {
    padding:'10px 12px', border:'1.5px solid #E2E8F0', borderRadius:'8px',
    fontSize:'16px', color:'#1E293B', background:'#F8FAFC',
    outline:'none', width:'100%', boxSizing:'border-box',
    transition:'border-color 0.15s, box-shadow 0.15s',
};

const rtd = { padding:'8px 12px', borderBottom:'1px solid #F1F5F9', color:'#334155', whiteSpace:'nowrap' };

